import { HttpResponse } from '@angular/common/http';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, Observable, race, switchMap, take, throwError } from 'rxjs';
import { callApi, callApiFailure } from '../state-files/action';
import { selectApiData } from '../state-files/selectors';

/** Collaborators {@link storeCachedPost} needs from the calling service. */
export interface StoreCachedPostDeps {
  /** NgRx store holding the `api.data` cache. */
  store: Store;
  /** Action stream, watched for the request's `callApiFailure`. */
  actions$: Actions;
  /**
   * Cache keys with a request in flight, owned by the calling service so that
   * concurrent callers of the same key share one dispatch.
   */
  fetchingKeys: Set<string>;
}

/**
 * POSTs through the NgRx `callApi` effect, served from the store cache when the
 * same endpoint + payload has already been fetched.
 *
 * The cache key is `${urlPath}:${JSON.stringify(params)}`. A request is
 * dispatched only when nothing is cached and none is in flight for that key;
 * every caller then waits for whichever comes first, the cached data or the
 * matching `callApiFailure`. The effect stores `{ data: res.body }`, so the
 * value is unwrapped back to the body and re-emitted as `{ body, status: 200 }`,
 * the shape the callers had from a direct HTTP call.
 * @param deps - Store, actions stream and the caller's in-flight key set.
 * @param urlPath - Endpoint path relative to the API base URL.
 * @param params - Request payload; also part of the cache key.
 * @returns Cold observable emitting one response and completing, or erroring
 *   with the effect's error (or `Error('API call failed')` when it has none).
 */
export function storeCachedPost<T>(
  deps: StoreCachedPostDeps,
  urlPath: string,
  params: unknown
): Observable<HttpResponse<T>> {
  const { store, actions$, fetchingKeys } = deps;
  const cacheKey = `${urlPath}:${JSON.stringify(params)}`;

  /** Emits the cached data once it lands, or errors with the request's failure. */
  // `any`: the api.data cache is untyped; the value is either the effect's { data } envelope or a bare body.
  const settled$ = (): Observable<any> => race(
    store.select(selectApiData(cacheKey)).pipe(
      filter((data) => Boolean(data)),
      take(1)
    ),
    actions$.pipe(
      ofType(callApiFailure),
      filter((action) => action.key === cacheKey),
      take(1),
      switchMap(({ error }) => throwError(() => error ?? new Error('API call failed')))
    )
  );

  // One flat chain: unsubscribing from the result tears down this caller's
  // store and action-stream listeners. The in-flight key is NOT tied to any
  // caller: it belongs to the dispatched request and is released only when that
  // request settles, so a caller cancelling early cannot let a later caller
  // dispatch a duplicate POST for a request still in flight.
  return store.select(selectApiData(cacheKey)).pipe(
    take(1),
    switchMap((res) => {
      if (!res && !fetchingKeys.has(cacheKey)) {
        fetchingKeys.add(cacheKey);
        const release = (): void => { fetchingKeys.delete(cacheKey); };
        // Owned by the request, not by a caller; self-completing (take(1) on
        // either outcome), so it ends exactly when the request settles.
        settled$().subscribe({ next: release, error: release });
        store.dispatch(
          callApi({
            method: 'POST',
            endpoint: urlPath,
            payload: params,
            key: cacheKey,
          })
        );
      }

      return settled$();
    }),
    map((data) => {
      const unwrappedData = data.data || data;
      return { body: unwrappedData, status: 200 } as unknown as HttpResponse<T>;
    })
  );
}
